import iaRequest from '@/iaRequest';
import { getEnv } from '@/utils/texts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { ATS_QUERY_KEYS } from './queryKeys';
import { IntegrationType } from '../..';

export interface IAtsConnectionResponse<T> {
  success: boolean;
  data: T;
  message: string;
}

export interface IAtsConnection {
  id: string;
  connectionId: string;
  integrationKey: string;
  atsName: string;
  companyId: number;
  createdBy: string;
  provider: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  lastSyncedAt: string | null;
  lastCronSyncAt: string | null;
  lastWebhookSyncedAt: string | null;
  hasNewJobs: boolean;
  syncConfigId: null | string;
  initialSyncDone: boolean;
  reportBack: 'automatic' | 'manual';
  logoUrl: string | null;
  autoSync: boolean;
  syncMode: 'automatic' | 'manual' | null;
  candidateStageSync: 'ats-only' | 'allow-updates';
  reportBackSupported: boolean;
}

interface IJobData {
  jobs: Job[];
  draft: Draft;
}
interface IAtsPermissionData {
  key:
    | 'ats_job_read'
    | 'ats_candidate_read'
    | 'ats_application_read'
    | 'ats_scorecard_write'
    | 'ats_interview_read'
    | 'ats_offer_read'
    | 'ats_document_read';
  label: string;
  description: string;
}

interface Draft {
  syncConfigId: string;
  selectedJobIds: string[];
  autoSync: boolean;
}

interface Job {
  atsJobId: string;
  title: string;
  department: string;
  location: string;
  country: string;
  remote: boolean;
  atsStatus: string;
  candidateCount: number;
  openedAt: string;
  alreadySynced: boolean;
  publicJobUrls?: string | null;
}

interface JobSelectionPayload {
  connectionId: string;
  selectedJobIds: string[];
  autoSync: boolean;
}

interface ConfigureSyncPayload {
  syncConfigId: string;
}
interface ReportBackPayload {
  syncConfigId: string;
  reportBack: string;
}
interface UpdateSyncModePayload {
  syncConfigId: string;
  syncMode: 'automatic' | 'manual';
}
interface UpdateCandidateStageSyncPayload {
  syncConfigId: string;
  candidateStageSync: 'ats-only' | 'allow-updates';
}
interface DisconnectAtsPayload {
  connectionId: string;
}

export interface DisconnectAtsResponse {
  status: 'disconnected' | 'already-disconnected';
}
interface ExistingJob {
  atsJobId: string;
  name: string;
  synced: boolean;
  jobId: number;
  department: string;
  createdAt: string;
  candidateCount: number;
  newCandidateCount: number;
  updatedCandidateCount: number;
  publicJobUrls?: string | null;
}

interface NewJob {
  atsJobId: string;
  name: string;
  isNew: boolean;
  department: string;
  createdAt: string;
  candidateCount: number;
  publicJobUrls?: string | null;
}

export interface SaveAtsConnectionPayload {
  atsName: string;
  connectionId: string;
  integrationKey: string;
  logoUrl: string;
}

export interface SavedAtsConnection {
  id: string;
  connectionId: string;
  integrationKey: string;
  atsName: string;
  logoUrl: string | null;
  status: string;
}

export interface TriggerManualSyncResponse {
  syncConfigId: string;
  newCandidatesDetected: boolean;
  newJobsDetected: boolean;
  newJobs?: NewJob[];
  existingJobs?: ExistingJob[];
}

export interface ConfirmNewJobsPayload {
  syncConfigId: string;
  selectedAtsJobIds: string[];
}

export const useSaveAtsConnection = () => {
  return useMutation({
    mutationFn: (payload: SaveAtsConnectionPayload) =>
      iaRequest
        .post<SaveAtsConnectionPayload, AxiosResponse<IAtsConnectionResponse<SavedAtsConnection>>>(
          'jobs/ats/connections',
          payload,
          {
            baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL'),
          },
        )
        .then(r => r.data),
  });
};

export const useGetAtsConnections = (activeTab?: IntegrationType, enabled?: boolean) => {
  return useQuery({
    queryKey: ATS_QUERY_KEYS.connections,
    queryFn: () =>
      iaRequest.get(`jobs/ats/connections`, {
        baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL'),
      }),
    select: (data: AxiosResponse<IAtsConnectionResponse<IAtsConnection[]>>) => data.data,
    enabled: activeTab === 'ATS' || (enabled ?? true),
  });
};

export const useGetAtsJobs = (connectionId: string, initialJobs?: TriggerManualSyncResponse) => {
  return useQuery({
    queryKey: ATS_QUERY_KEYS.jobs(connectionId),
    queryFn: () =>
      iaRequest.get(`/jobs/ats/connections/${connectionId}/jobs`, {
        baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL'),
      }),
    select: (data: AxiosResponse<IAtsConnectionResponse<IJobData>>) => data.data,
    enabled: !!connectionId && !initialJobs,
    staleTime: 0,
  });
};

export const useSaveJobSelection = () => {
  return useMutation({
    mutationFn: (payload: JobSelectionPayload) =>
      iaRequest
        .post<JobSelectionPayload, AxiosResponse<IAtsConnectionResponse<{ syncConfigId: string }>>>(
          'jobs/ats/connections/sync/draft',
          payload,
          {
            baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL'),
          },
        )
        .then(r => r.data),
  });
};

export const useConfigureSync = () => {
  return useMutation({
    mutationFn: (payload: ConfigureSyncPayload) =>
      iaRequest
        .post<ConfigureSyncPayload, AxiosResponse<IAtsConnectionResponse<{ syncConfigId: string }>>>(
          'jobs/ats/connections/sync/start',
          payload,
          {
            baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL'),
          },
        )
        .then(r => r.data),
  });
};

export const useReportBack = () => {
  return useMutation({
    mutationFn: (payload: ReportBackPayload) =>
      iaRequest
        .patch<ReportBackPayload, AxiosResponse<IAtsConnectionResponse<{ syncConfigId: string }>>>(
          'jobs/ats/connections/sync/report-back',
          payload,
          {
            baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL'),
          },
        )
        .then(r => r.data),
  });
};

export const useUpdateSyncMode = () => {
  return useMutation({
    mutationFn: (payload: UpdateSyncModePayload) =>
      iaRequest
        .patch<UpdateSyncModePayload, AxiosResponse<IAtsConnectionResponse<{ syncConfigId: string }>>>(
          'jobs/ats/connections/sync/mode',
          payload,
          {
            baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL'),
          },
        )
        .then(r => r.data),
  });
};

export const useUpdateCandidateStageSync = () => {
  return useMutation({
    mutationFn: (payload: UpdateCandidateStageSyncPayload) =>
      iaRequest
        .patch<UpdateCandidateStageSyncPayload, AxiosResponse<IAtsConnectionResponse<{ syncConfigId: string }>>>(
          'jobs/ats/connections/sync/candidate-stage-mode',
          payload,
          {
            baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL'),
          },
        )
        .then(r => r.data),
  });
};

export const useDisconnectAts = () => {
  return useMutation({
    mutationFn: ({ connectionId }: DisconnectAtsPayload) =>
      iaRequest
        .post<never, AxiosResponse<IAtsConnectionResponse<DisconnectAtsResponse>>>(
          `jobs/ats/connections/${connectionId}/disconnect`,
          undefined,
          {
            baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL'),
          },
        )
        .then(r => r.data),
  });
};

export const useTriggerManualSync = () => {
  return useMutation({
    mutationFn: (payload: { syncConfigId: string }) =>
      iaRequest
        .post<ConfigureSyncPayload, AxiosResponse<IAtsConnectionResponse<TriggerManualSyncResponse>>>(
          'jobs/ats/connections/sync/trigger',
          payload,
          {
            baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL'),
          },
        )
        .then(r => r.data),
  });
};

export const useConfirmNewJobs = () => {
  return useMutation({
    mutationFn: (payload: ConfirmNewJobsPayload) =>
      iaRequest
        .post<ConfirmNewJobsPayload, AxiosResponse<IAtsConnectionResponse<{ syncConfigId: string }>>>(
          'jobs/ats/connections/sync/confirm-new-jobs',
          payload,
          {
            baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL'),
          },
        )
        .then(r => r.data),
  });
};

export const useSendAtsReport = () => {
  return useMutation({
    mutationFn: (interviewDetailId: string) =>
      iaRequest
        .post<never, AxiosResponse<{ message: string }>>(`/ats-report/send/${interviewDetailId}`)
        .then(r => r.data),
  });
};

export const useSendAtsNotes = () => {
  return useMutation({
    mutationFn: (interviewDetailId: string) =>
      iaRequest
        .post<never, AxiosResponse<{ message: string }>>(`/ia-report/${interviewDetailId}/notes/send-to-ats`)
        .then(r => r.data),
  });
};

export interface UnifiedIntegrationApiUrl {
  title?: string;
  api_url: string;
}

export interface UnifiedIntegrationDetail {
  type?: string;
  name: string;
  logo_url?: string;
  auth?: {
    token?: boolean;
    oauth?: boolean;
    other?: boolean;
  };
  token_names?: string[];
  token_instructions?: string[];
  api?: {
    urls?: UnifiedIntegrationApiUrl[];
  };
}

export interface CreateAtsTokenConnectionPayload {
  integrationKey: string;
  token?: string;
  tokens: Record<string, string>;
  apiUrl?: string;
  atsName: string;
  logoUrl: string;
}

export const useCreateAtsTokenConnection = () => {
  return useMutation({
    mutationFn: (payload: CreateAtsTokenConnectionPayload) =>
      iaRequest
        .post<
          CreateAtsTokenConnectionPayload,
          AxiosResponse<IAtsConnectionResponse<{ connectionId: string }>>
        >('jobs/ats/connections/token', payload, { baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL') })
        .then(r => r.data),
  });
};

export const useAtsIntegrationList = () => {
  const workspaceId = getEnv('VITE_UNIFIED_WORKSPACE_ID');
  return useQuery<UnifiedIntegrationDetail[]>({
    queryKey: ['ats-integrations', workspaceId],
    queryFn: async () => {
      const res = await fetch(
        `https://api.unified.to/unified/integration/workspace/${workspaceId}?&categories=ats&auth_type=token`,
      );
      const data: UnifiedIntegrationDetail[] = await res.json();
      return data.filter(i => i.logo_url);
    },
    staleTime: Infinity,
  });
};

export const useGetAtsPermission = (type: string) => {
  return useQuery({
    queryKey: ['GET_ATS_PERMISSION', type],
    queryFn: () =>
      iaRequest.get(`/jobs/ats/integrations/${type}/permissions`, {
        baseURL: getEnv('VITE_JOB_CAMPAIGN_BASE_URL'),
      }),
    select: (data: AxiosResponse<IAtsConnectionResponse<IAtsPermissionData[]>>) => data.data.data,
    enabled: !!type,
  });
};
